/**
 * "Interference" — the Nodal Walk. Two wave sources flood a pool. The standing
 * interference pattern has ANTINODES (crests reinforce — violent, they throw
 * you back) and NODAL LINES (crest meets trough — dead calm, safe). A phase
 * dial shifts the pattern: twist it and the central corridor flips from a
 * churning antinode to a calm node you can walk straight up to the exit.
 *
 * Danger at a point is the time-independent amplitude envelope
 *   2·|cos((k·(r1−r2) − φ) / 2)|   (0 = node, 2 = antinode),
 * which the scene's shader visualizes with the same constants. Pure/deterministic.
 */
export interface CrossSource {
  x: number;
  z: number;
}

export interface InterferenceState {
  t: number;
  pos: { x: number; z: number };
  facing: number;
  /** Player-tuned phase offset between the sources (radians). */
  phi: number;
  strideAccum: number;
  /** Amplitude envelope at the player (0 calm .. 2 violent). */
  danger: number;
  hitFlash: number;
  exited: boolean;
}

export interface InterferenceInput {
  move: { x: number; y: number };
  /** Phase dial rate, -1..1 (right stick x). */
  phase: number;
}

export interface InterferenceConfig {
  speed: number;
  xMin: number;
  xMax: number;
  zMin: number;
  zMax: number;
  sources: [CrossSource, CrossSource];
  k: number; // wavenumber
  omega: number; // angular frequency (visual ripple speed)
  phaseSpeed: number; // rad/s the dial sweeps φ
  dangerThreshold: number; // envelope above which you get thrown
  knockbackZ: number;
  exitZ: number;
  exitHalfW: number;
  playerRadius: number;
  strideLength: number;
}

export type InterferenceEvent = { type: 'footstep' } | { type: 'hit' } | { type: 'exit' };

export const DEFAULT_INTERFERENCE_CONFIG: InterferenceConfig = {
  speed: 4.2,
  xMin: -7,
  xMax: 7,
  zMin: 0,
  zMax: 26,
  sources: [
    { x: -4, z: 24 },
    { x: 4, z: 24 },
  ],
  k: 1.15,
  omega: 3,
  phaseSpeed: 2.4,
  dangerThreshold: 1.2,
  knockbackZ: 3,
  exitZ: 22,
  exitHalfW: 2.2,
  playerRadius: 0.5,
  strideLength: 1.4,
};

/** Time-independent amplitude envelope (0 node .. 2 antinode). */
export function envelopeAt(px: number, pz: number, phi: number, cfg: InterferenceConfig): number {
  const r1 = Math.hypot(px - cfg.sources[0].x, pz - cfg.sources[0].z);
  const r2 = Math.hypot(px - cfg.sources[1].x, pz - cfg.sources[1].z);
  return 2 * Math.abs(Math.cos((cfg.k * (r1 - r2) - phi) / 2));
}

export function createInterferenceState(): InterferenceState {
  return {
    t: 0,
    pos: { x: 0, z: 2 },
    facing: 0,
    phi: 0,
    strideAccum: 0,
    danger: 2,
    hitFlash: 0,
    exited: false,
  };
}

export function stepInterference(
  s: InterferenceState,
  input: InterferenceInput,
  dt: number,
  cfg: InterferenceConfig,
): InterferenceEvent[] {
  const events: InterferenceEvent[] = [];
  s.t += dt;
  if (s.hitFlash > 0) s.hitFlash = Math.max(0, s.hitFlash - dt);
  if (s.exited) return events;

  // Tune the phase (wrap to 0..2π).
  s.phi += input.phase * cfg.phaseSpeed * dt;
  s.phi = ((s.phi % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

  // Move.
  const mx = input.move.x;
  const mz = -input.move.y;
  const len = Math.hypot(mx, mz);
  const px = s.pos.x;
  const pz = s.pos.z;
  if (len > 0.01) {
    s.facing = Math.atan2(mx, mz);
    s.pos.x += (mx / len) * cfg.speed * dt * Math.min(1, len);
    s.pos.z += (mz / len) * cfg.speed * dt * Math.min(1, len);
  }
  const r = cfg.playerRadius;
  s.pos.x = Math.max(cfg.xMin + r, Math.min(cfg.xMax - r, s.pos.x));
  s.pos.z = Math.max(cfg.zMin + r, Math.min(cfg.zMax - r, s.pos.z));

  // Footsteps.
  const moved = Math.hypot(s.pos.x - px, s.pos.z - pz);
  s.strideAccum += moved;
  while (s.strideAccum >= cfg.strideLength) {
    s.strideAccum -= cfg.strideLength;
    events.push({ type: 'footstep' });
  }

  // Antinode throws you back.
  s.danger = envelopeAt(s.pos.x, s.pos.z, s.phi, cfg);
  if (s.danger > cfg.dangerThreshold && s.hitFlash <= 0) {
    s.pos.z = cfg.knockbackZ;
    s.pos.x *= 0.4;
    s.hitFlash = 0.6;
    events.push({ type: 'hit' });
    return events;
  }

  // Reached the calm exit between the sources.
  if (s.pos.z >= cfg.exitZ && Math.abs(s.pos.x) <= cfg.exitHalfW) {
    s.exited = true;
    events.push({ type: 'exit' });
  }

  return events;
}
